// ============================================
// Risk Radar — "Summarise it for me", Liquidity layer
//
// Same design as Exposure (summary.ts) and Flow toxicity (flowSummary.ts):
// the button freezes the LP view on screen (group, period, instrument), every
// figure is written here with the page's own formatters, the AI only words
// the facts (the server rejects any number not in them), and the panel says
// what has moved since (driftOfLiq).
//
// The question it answers, per LP: is this LP a risk to me now, and will it
// become one. "Now" is the backend's verdict and its reasons (verdictLines,
// the drawer's own sentences). "Will it become one" needs 30 days of daily
// figures (phase 2): until the payload carries an outlook, the summary says it
// is not measured, never guesses a trend.
//
// Pure: no React, no fetching.
// ============================================

import type { LiqLevel } from '@/services/api';
import { fmtUsd, fmtUtcDateTime, type Band } from './model';
import {
  causeText,
  dimensionLabel,
  fmtCount,
  fmtFrac,
  fmtMs,
  levelRank,
  periodText,
  verdictLines,
  type LiqView,
  type LpRow,
} from './liquidityModel';
import type { Drift, FrozenSummary, KeyFigure, SummaryFact, SummaryText } from './summary';

export interface LiqFingerprint {
  group: string;
  period: string;
  symbol: string | null;
  worst: string | null;
  levels: Record<string, LiqLevel | null>;
  atRisk: number;
  rejectShare: number | null;
}

const TOP_N = 6;
const MAX_FACT = 590;
/** Days of daily figures the outlook needs (Liquidity brief §2.4). */
const OUTLOOK_DAYS = 30;

function levelWord(l: LiqLevel | null): string {
  return l ?? 'not judged';
}

function bandOfLevel(l: LiqLevel | null): Band | null {
  return l ? (l.toLowerCase() as Band) : null;
}

function cap(text: string): string {
  return text.length <= MAX_FACT ? text : `${text.slice(0, MAX_FACT - 1).replace(/[\s,;:]+\S*$/, '')}…`;
}

function groupWord(lv: LiqView): string {
  return lv.group === 'test' ? 'test LPs (simulated, not real counterparties)' : 'real LPs';
}

function rejectRateOf(r: LpRow): number | null {
  const v = r.dimById.get('rejecting')?.metrics.find((m) => m.key === 'reject_rate')?.m.value;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function stateWords(r: LpRow): string | null {
  if (!r.state) return null;
  const s = r.state === 'CONNECTED' ? 'connected' : r.state === 'DEGRADED' ? 'degraded' : r.state === 'DISCONNECTED' ? 'disconnected now' : null;
  return s;
}

/** One LP, in one fact: level, what set it, orders, state, money. */
function lpFact(r: LpRow, lv: LiqView, i: number): string {
  const f = r.figures;
  const head = [
    `${r.name}${i === 0 && r.level ? ' (worst of the group)' : ''}: level ${levelWord(r.level)}`,
    `${fmtCount(r.orders)} orders`,
    stateWords(r),
    r.openHedges ? `${fmtCount(r.openHedges)} open hedges at it` : null,
  ].filter(Boolean).join(', ');
  const why = r.level === null
    ? [`too few orders to judge${lv.minOrders ? ` (under ${lv.minOrders})` : ''}`]
    : verdictLines(r, lv.group, lv.minOrders).slice(0, 3).map((l) => l.replace(/\.$/, ''));
  const money: string[] = [];
  if (f.lp_slippage_usd != null) {
    money.push(f.lp_slippage_usd > 0
      ? `its own slippage cost the broker ${fmtUsd(f.lp_slippage_usd)}`
      : f.lp_slippage_usd < 0 ? `its own slippage was ${fmtUsd(-f.lp_slippage_usd)} in the broker's favour` : 'its own slippage cost nothing');
  } else if ((f.basis_slippage_fills ?? 0) > 0) {
    money.push('it sends no quotes of its own, so its slippage is not rated');
  }
  const av = f.availability;
  if (av && (av.disconnects ?? 0) > 0) money.push(`${fmtCount(av.disconnects)} session drop${av.disconnects === 1 ? '' : 's'} by the LP or the network`);
  return cap(`${head}. Why: ${why.join('; ')}${money.length ? `. Also: ${money.join('; ')}` : ''}.`);
}

/**
 * @param lv  the LP view on screen (group, period, instrument), frozen
 */
export function freezeLiquidity(lv: LiqView, asOf: number): FrozenSummary<LiqFingerprint> {
  const facts: SummaryFact[] = [];
  const add = (id: string, text: string) => facts.push({ id, text: cap(text) });
  const per = periodText(lv.period);
  const n = lv.rows.length;
  const atRisk = lv.counts.HIGH + lv.counts.CRITICAL;
  const t = lv.totals;
  const atRiskShare = t.orders > 0 ? t.ordersAtRisk / t.orders : null;
  const rejectShare = t.orders > 0 ? t.lpRejects / t.orders : null;
  const judged = lv.rows.filter((r) => r.level !== null);
  const unjudged = lv.rows.filter((r) => r.level === null);

  // ── Moment, scope, method ──
  add('time', `Snapshot summarised: ${fmtUtcDateTime(asOf)}. It covers the ${per}, ${groupWord(lv)}${lv.symbol ? `, ${lv.symbol} only` : ', all instruments'}. Valid for that moment only; the live page moves on.`);
  add('method', 'Each LP is judged on four dimensions: Rejecting (does it fill our orders), Slipping (at the price it showed), Slow (its own time to answer) and Availability (is it there in trading hours), against the broker\'s thresholds in Settings, Liquidity. Its level is its worst dimension; two dimensions at High make it Critical. Taiga\'s own delay is never counted against an LP.');
  if (lv.group === 'test') add('test', 'These are test LPs fed by simulators: their figures check the platform, they say nothing about a real counterparty.');

  // ── The group ──
  add('count', `${atRisk} of ${n} ${lv.group === 'test' ? 'test LPs' : 'LPs'} at High or Critical. By level: Critical ${lv.counts.CRITICAL}, High ${lv.counts.HIGH}, Warning ${lv.counts.WARNING}, Low ${lv.counts.LOW}${lv.tooFew ? `; not judged (too few orders) ${lv.tooFew}` : ''}.${atRiskShare !== null && atRisk > 0 ? ` The LPs at High or Critical received ${fmtFrac(atRiskShare)} of the orders.` : ''}`);
  if (t.orders > 0) {
    const worstRej = [...judged].filter((r) => rejectRateOf(r) !== null).sort((a, b) => (rejectRateOf(b) ?? 0) - (rejectRateOf(a) ?? 0))[0];
    add('rejects', `Rejected by the LPs: ${fmtFrac(rejectShare)}, ${fmtCount(t.lpRejects)} of ${fmtCount(t.orders)} orders${worstRej ? `; most at ${worstRej.name}, ${fmtFrac(rejectRateOf(worstRej))}` : ''}.${t.taigaRejects > 0 ? ` Another ${fmtCount(t.taigaRejects)} were refused by Taiga before sending, not counted against any LP.` : ''}`);
  }

  // ── The LPs, worst first ──
  const top = lv.rows.slice(0, TOP_N);
  top.forEach((r, i) => add(`lp_${r.lpId}`, lpFact(r, lv, i)));
  if (lv.rows.length > TOP_N) add('rest', `${lv.rows.length - TOP_N} more LPs in the group, at lower levels.`);
  const silent = unjudged.filter((r) => r.orders === 0).map((r) => `${r.name}${r.state === 'DISCONNECTED' ? ' (disconnected)' : ''}`);
  if (silent.length) add('silent', `LPs with no order in the period, so not judged: ${silent.join(', ')}.`);

  // ── Ours, not the LPs' ──
  const own = lv.rows.reduce((s, r) => s + (r.figures.availability?.own_disconnects ?? 0), 0);
  if (own > 0) {
    const causes = new Set(lv.rows.flatMap((r) => r.disconnectEvents.filter((e) => e.own).map((e) => causeText(e.cause))));
    add('own_drops', `${fmtCount(own)} session drops were ours (${[...causes].join(', ') || 'restarts'}), not counted against any LP.`);
  }
  const taiga = lv.rows
    .map((r) => r.brokerOwn?.m.value)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (taiga.length) add('taiga_time', `Taiga's own time from a client deal seen to the order sent, p95, slowest LP route: ${fmtMs(Math.max(...taiga))}. It is the broker's own part of the delay, never an LP's.`);

  // ── Will it become a risk ──
  const days = Math.max(0, ...lv.rows.map((r) => r.byDay.length));
  add('outlook', days >= OUTLOOK_DAYS
    ? 'Daily figures for 30 days exist, but the payload carries no trend yet: whether an LP is getting worse is not stated.'
    : `Whether an LP is becoming a risk is not measured yet: the trend compares the last 7 days with the 30 before, and ${days === 0 ? 'no day is' : `${days} day${days === 1 ? ' is' : 's are'}`} stored so far. Say only what each LP is now.`);

  // ── What the reader can open ──
  add('pages', 'Pages the risk manager can open: the LP in Risk Radar (select it) with its Evidence, the orders behind each figure; Thresholds (Settings, Liquidity); Hedging Strategies, where the hedges are routed to the LPs. Nothing is executed from Risk Radar.');

  // ── Key figures ──
  const keyFigures: KeyFigure[] = [
    {
      label: `${lv.group === 'test' ? 'Test LPs' : 'LPs'} at High or Critical`,
      value: `${atRisk} of ${n}`,
      note: atRiskShare !== null && atRisk > 0 ? `${fmtFrac(atRiskShare)} of orders · ${per}` : per,
    },
    {
      label: 'Rejected by the LPs',
      value: fmtFrac(rejectShare),
      note: `${fmtCount(t.lpRejects)} of ${fmtCount(t.orders)} orders`,
    },
    ...top.slice(0, 4).map((r) => ({
      label: r.name,
      value: levelWord(r.level),
      note: r.level === null
        ? `${fmtCount(r.orders)} orders, not judged`
        : `${fmtCount(r.orders)} orders · ${r.dims.filter((d) => levelRank(d.level) === levelRank(r.level)).map((d) => dimensionLabel(d.id)).join(', ')}`,
      band: bandOfLevel(r.level),
    })),
  ];

  const levels: Record<string, LiqLevel | null> = {};
  for (const r of lv.rows) levels[r.lpId] = r.level;

  return {
    asOf,
    takenAt: Date.now(),
    facts: facts.slice(0, 40),
    keyFigures,
    fallback: fallbackText(lv, top, atRisk, days),
    fingerprint: {
      group: lv.group, period: lv.period, symbol: lv.symbol,
      worst: lv.worst?.lpId ?? null,
      levels, atRisk, rejectShare,
    },
  };
}

function fallbackText(lv: LiqView, top: LpRow[], atRisk: number, days: number): SummaryText {
  const per = periodText(lv.period);
  const worst = lv.worst;
  const n = lv.rows.length;
  const headline = worst
    ? `${atRisk} of ${n} ${lv.group === 'test' ? 'test LPs' : 'LPs'} at High or Critical over the ${per}; the worst is ${worst.name}, ${levelWord(worst.level)}.`
    : `No ${lv.group === 'test' ? 'test LP' : 'LP'} has enough orders in the ${per} to judge.`;

  const points: string[] = [];
  for (const r of top.filter((x) => x.level !== null && levelRank(x.level) >= levelRank('WARNING')).slice(0, 2)) {
    const why = verdictLines(r, lv.group, lv.minOrders)[0];
    if (why) points.push(`${r.name}, ${levelWord(r.level)}. ${why}`);
  }
  if (lv.group === 'test') points.push('These are test LPs fed by simulators: the figures check the platform, not a real counterparty.');
  if (days < OUTLOOK_DAYS) points.push('Whether an LP is getting worse is not measured yet: it needs 30 days of daily figures.');

  const actions: string[] = [];
  if (worst && levelRank(worst.level) >= levelRank('WARNING')) {
    const dim = worst.dims.find((d) => d.level === worst.level);
    actions.push(`Open ${worst.name} and check its Evidence${dim ? ` for ${dimensionLabel(dim.id)}` : ''}.`);
    if (levelRank(worst.level) >= levelRank('HIGH') && worst.orders > 0) actions.push(`Review in Hedging Strategies how much is routed to ${worst.name}.`);
  }
  if (lv.tooFew > 0) actions.push('Check why some LPs have too few orders to judge.');

  return { headline, points: points.slice(0, 3), actions: actions.slice(0, 3) };
}

// ── What has moved since ─────────────────────────────────────

export function driftOfLiq(f: LiqFingerprint, now: LiqView): Drift {
  const lines: string[] = [];
  let stale = false;
  if (now.group !== f.group || now.period !== f.period || now.symbol !== f.symbol) {
    lines.push(`The page now shows ${now.group === 'test' ? 'test LPs' : 'real LPs'}, ${periodText(now.period)}${now.symbol ? `, ${now.symbol}` : ''}`);
    return { stale: true, lines };
  }
  const atRisk = now.counts.HIGH + now.counts.CRITICAL;
  if (atRisk !== f.atRisk) { lines.push(`At High or Critical ${f.atRisk} → ${atRisk}`); stale = true; }
  const worst = now.worst?.lpId ?? null;
  if (worst !== f.worst) {
    lines.push(`Worst LP ${f.worst ? now.byId.get(f.worst)?.name ?? f.worst : 'none'} → ${now.worst?.name ?? 'none'}`);
    stale = true;
  }
  for (const [id, was] of Object.entries(f.levels)) {
    const r = now.byId.get(id);
    const is = r?.level ?? null;
    if (is !== was) { lines.push(`${r?.name ?? id} ${levelWord(was)} → ${levelWord(is)}`); stale = true; }
  }
  const t = now.totals;
  const rej = t.orders > 0 ? t.lpRejects / t.orders : null;
  if (rej !== null && f.rejectShare !== null && fmtFrac(rej) !== fmtFrac(f.rejectShare)) {
    lines.push(`Rejected ${fmtFrac(f.rejectShare)} → ${fmtFrac(rej)}`);
    if (Math.abs(rej - f.rejectShare) >= 0.001 && Math.abs(rej - f.rejectShare) / Math.max(f.rejectShare, 1e-9) >= 0.05) stale = true;
  }
  return { stale, lines };
}
